import type { AxiosInstance } from 'axios'
import axios from 'axios'
import {
	createContext,
	type PropsWithChildren,
	useContext,
	useMemo,
} from 'react'
import { baseURL as defaultBaseURL } from './api'
import { createClientInstance } from './client'

export interface EntityParams {
	/** Prefix for entity list filter params, e.g. 'filter_'. Default ''. */
	filterPrefix: string
	/** Page-size param name. Default 'limit'. */
	pageSizeParam: string
}

const defaultEntityParams: EntityParams = {
	filterPrefix: '',
	pageSizeParam: 'limit',
}

export interface ApiProviderValue {
	baseURL: string
	entityParams: EntityParams
	/**
	 * @deprecated The method should not be used, use `client` instead.
	 */
	axios: AxiosInstance
	client: ReturnType<typeof createClientInstance>
}

const Context = createContext<ApiProviderValue>({
	baseURL: defaultBaseURL,
	entityParams: defaultEntityParams,
	axios: axios.create({
		baseURL: defaultBaseURL,
		withCredentials: true,
	}),
	client: createClientInstance({
		baseUrl: defaultBaseURL,
	}),
})

export const useApi = () => {
	const value = useContext(Context)

	// if (!value) {
	//   throw Error(`useApi should be called within an ApiProvider`)
	// }

	return value
}

export default function Provider({
	baseURL = defaultBaseURL,
	entityParams,
	...props
}: PropsWithChildren<{
	baseURL?: string
	entityParams?: Partial<EntityParams>
}>) {
	// Destructured so an inline `entityParams` object does not break the memo.
	const { filterPrefix, pageSizeParam } = {
		...defaultEntityParams,
		...entityParams,
	}

	const value = useMemo(
		() => ({
			baseURL,
			entityParams: { filterPrefix, pageSizeParam },
			axios: axios.create({
				baseURL,
				withCredentials: true,
			}),
			client: createClientInstance({
				baseUrl: baseURL,
			}),
		}),
		[baseURL, filterPrefix, pageSizeParam],
	)

	return <Context.Provider {...props} value={value} />
}

export { Provider as ApiProvider }
